/**
 * リクエストファイルアップロード時の確認ダイアログの(予約APIをDryRunモードで実行)
 * -エラーが含まれない場合
 * リクエストファイルアップロード時の確認ダイアログ(予約APIをDryRunモードで実行)
 * -エラーが含まれる場合
 * リクエストファイルアップロード後のエラーダイアログ(予約APIを通常モードで実行)
 * -エラーが含まれる場合
 */

import { Button, Popup, Title } from 'common/components';
import { BoardGL, Glossary } from 'common/utils/glossary';
import { PostDevicesActivationReserveResponse } from 'common/utils/request/sas-response';

// DEVICE_ACTIVATE_UPLOAD_RESULT_DISP

interface BoardProps {
  /** ReqFileUploadResultBoardの表示有無 */
  isDisplay: boolean;
  /** グロッサリ */
  glossary: Glossary;
  /** 予約APIの実行結果 */
  reserveResponse: PostDevicesActivationReserveResponse;
  /** "閉じるボタン"(×)、"いいえ(キャンセル)ボタン"、"OKボタン"の処理 */
  onClickClose: () => void;
  /** "はい(開始)ボタン"の処理 */
  onClickYes: () => void;
}

export const ReqFileUploadResultBoard: React.FC<BoardProps> = ({
  isDisplay,
  glossary,
  reserveResponse,
  onClickClose,
  onClickYes
}) => {
  /** レスポンスで受け取ったコードをメッセージに変換 */
  const changeMessage = (errorCode: string) => {
    switch (errorCode) {
      case 'WAR001': {
        return glossary.glx(BoardGL.ReqFileUploadWarningNotActivated);
      }
      case 'WAR002': {
        return glossary.glx(BoardGL.ReqFileUploadWarningActivatedSameTime);
      }
      case 'WAR003': {
        return glossary.glx(BoardGL.ReqFileUploadWarningAlreadyActivated);
      }
      case 'ERR001':
      case 'ERR002':
      case 'ERR003':
      case 'ERR004':
      case 'ERR005': {
        return glossary.glx(BoardGL.ReqFileUploadErrorNotAvailable);
      }
      case 'ERR006': {
        return glossary.glx(BoardGL.ReqFileUploadErrorExcessCameras);
      }
      case 'ERR007': {
        return glossary.glx(BoardGL.ReqFileUploadErrorNotSupported);
      }
      case 'ERR008': {
        return glossary.glx(BoardGL.ReqFileUploadErrorNotActivated);
      }
      case 'ERR009': {
        return glossary.glx(BoardGL.ReqFileUploadErrorAlreadyActivated);
      }
      case 'ERR010': {
        return glossary.glx(BoardGL.ReqFileUploadErrorNotMatch);
      }
      case 'ERR011': {
        return glossary.glx(BoardGL.ReqFileNecessaryNotContainedFailure);
      }
    }
  };

  /**
   * エラーの結果を表示するダイアログ/成功&警告を表示するダイアログのどちらを表示するか
   * true: エラーダイアログ
   * false: 成功&警告ダイアログ
   *  */
  const isErrorDisp = reserveResponse.errorDevices.length > 0;

  /** classNameがreqFileUploadResultBoard-responseの要素をすべて取得 */
  const boardResponses = document.querySelectorAll(
    '.reqFileUploadResultBoard-response'
  );
  /** 取得した要素の中から最後の要素を見つけて、特定のクラスを追加
   * 線の重なりをなくすために最後の要素を判別してスタイルを適用するために特定のクラスを使用
   */
  if (boardResponses.length > 0) {
    boardResponses[boardResponses.length - 1].classList.add('last-response');
  }

  const _component = (
    <Popup
      className="reqFileUploadResultBoard-popup"
      onClickBackground={onClickClose}
      data-test="reqFileUploadResultBoard-popup"
      visible={isDisplay}
    >
      <div className="reqFileUploadResultBoard">
        <Button
          type="toolButton"
          subType="close"
          data-test="reqFileUploadResultBoard-toolButton"
          onClick={onClickClose}
        />
        {!isErrorDisp ? (
          <Title type="title" data-test="reqFileUploadResultBoard-listMessage">
            {reserveResponse.warningDevices.length === 0 ? (
              <span
                className="okIcon"
                data-test="reqFileUploadResultBoard-listMessage-okIcon"
              />
            ) : (
              <span
                className="warningIcon"
                data-test="reqFileUploadResultBoard-listMessage-warningIcon"
              />
            )}
            <p>{glossary.glx(BoardGL.ReqFileUploadConfirmation)}</p>
          </Title>
        ) : (
          <Title type="title" data-test="reqFileUploadResultBoard-listMessage">
            <span
              className="errorIcon"
              data-test="reqFileUploadResultBoard-listMessage-errorIcon"
            />
            {glossary.glx(BoardGL.ReqFileNotUpload)}
          </Title>
        )}
        <div className="reqFileUploadResultBoard-responseContainer">
          {reserveResponse.successDevices.length > 0 && !isErrorDisp && (
            <div>
              {reserveResponse.successDevices.map((response) => (
                <div
                  className="reqFileUploadResultBoard-response"
                  data-test="reqFileUploadResultBoard-success-response"
                >
                  <div className="reqFileUploadResultBoard-modelSerial">
                    <span
                      className="okIcon"
                      data-test="reqFileUploadResultBoard-okIcon"
                    />
                    <p>
                      {response.modelName} ({response.serialNo})
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
          {reserveResponse.warningDevices.length > 0 && !isErrorDisp && (
            <div>
              {reserveResponse.warningDevices.map((response) => (
                <div
                  className="reqFileUploadResultBoard-response"
                  data-test="reqFileUploadResultBoard-warning-response"
                >
                  <div className="reqFileUploadResultBoard-modelSerial">
                    <span
                      className="warningIcon"
                      data-test="reqFileUploadResultBoard-warningIcon"
                    />
                    <p>
                      {response.modelName} ({response.serialNo})
                    </p>
                  </div>
                  {response.errorDetails.map((error) => (
                    <>
                      <p>{changeMessage(error.code)}</p>
                      {Object.values(error.services).map((service) => (
                        <p>{service}</p>
                      ))}
                    </>
                  ))}
                </div>
              ))}
            </div>
          )}
          {reserveResponse.errorDevices.length > 0 && (
            <div>
              {reserveResponse.errorDevices.map((response) => (
                <div
                  className="reqFileUploadResultBoard-response"
                  data-test="reqFileUploadResultBoard-error-response"
                >
                  <div className="reqFileUploadResultBoard-modelSerial">
                    <span
                      className="errorIcon"
                      data-test="reqFileUploadResultBoard-errorIcon"
                    />
                    <p>
                      {response.modelName} ({response.serialNo})
                    </p>
                  </div>
                  {response.errorDetails.map((error) => (
                    <>
                      <p>{changeMessage(error.code)}</p>
                    </>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
        {!isErrorDisp ? (
          <div className="reqFileUploadResultBoard-buttonContainer">
            <Button
              type="okButton"
              data-test="reqFileUploadResultBoard-yesButton"
              onClick={onClickYes}
            >
              {glossary.glx(BoardGL.UploadStarted)}
            </Button>
            <Button
              type="cancelButton"
              data-test="reqFileUploadResultBoard-noButton"
              onClick={onClickClose}
            >
              {glossary.glx(BoardGL.Canceled)}
            </Button>
          </div>
        ) : (
          <Button
            type="cancelButton"
            data-test="reqFileUploadResultBoard-closeButton"
            onClick={onClickClose}
            className="reqFileUploadResultBoard-closeButton"
          >
            {glossary.glx(BoardGL.ActivateBoardOK)}
          </Button>
        )}
      </div>
    </Popup>
  );
  return isDisplay ? _component : null;
};

export default ReqFileUploadResultBoard;
